import { useEffect, useState } from 'react'
import { getTokenClaims } from '@api/services/sessionService'
import { ownDeliveryAddress } from './emailRecipients'

/**
 * Spec 138 §3.7 — the address the owner's OWN copy of a scheduled report goes to, for
 * the "You — <address>" line of a NEW schedule.
 *
 * Read from the decoded ACCESS token, which is the token cb-api receives and the claim
 * it binds delivery to (`parseAuth`). Not `useAuth0().user.email` (the ID token) and
 * not `/users/me` (which may answer CB's registry address, or a display name, when the
 * claim is absent): either could name an address the server will never send to, and
 * this line is a promise about where the figures go.
 *
 * `{ status, address }`:
 *   loading   not read yet
 *   ready     `address` is the usable address, or null when the account has none
 *   unknown   the token could not be read — the field then says nothing it cannot back
 *
 * The schedule EDITOR reads this too (spec 139, *review*), and prefers it to the row's
 * own `deliverTo`, which is only what the server resolved at the last write: an account
 * that has lost or gained an address since is judged by its token, as the server judges
 * the save. The row stands in only while this is loading, or when it is `unknown`.
 */
export default function useOwnDeliveryAddress(enabled = true) {
  const [state, setState] = useState({ status: 'loading', address: null })
  useEffect(() => {
    if (!enabled) return undefined
    let alive = true
    getTokenClaims()
      .then((claims) => { if (alive) setState({ status: 'ready', address: ownDeliveryAddress(claims) }) })
      .catch(() => { if (alive) setState({ status: 'unknown', address: null }) })
    return () => { alive = false }
  }, [enabled])
  return state
}
